import { useShellText } from "../../i18n/useShellText";
import { useEffect, useRef, useState } from "react";
import { useTheme, type ThemeName } from "../theme/ThemeProvider";
import { themeLabels } from "../theme/themeLabels";
import { cn } from "../../utils/cn";
import { trackEvent } from "../../lib/auditCollector";
import { Icon } from "../ui/Icon";
import { MenuPortal } from "./MenuPortal";
import { useMenuPosition } from "./useMenuPosition";

const themes: ThemeName[] = ["spectre", "reel", "smoked", "defaulted"];

export const ThemeMenu = () => {
  const { t } = useShellText();
  const { theme, setTheme } = useTheme();
  const labels = themeLabels(t, t($ => $.defaultTheme));
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuStyle = useMenuPosition(anchorRef, open, { align: "right" });

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        ref={anchorRef}
        aria-label={t($ => $.themeName, { theme: labels[theme] })}
        className="flex items-center gap-2 rounded-md border border-border/50 bg-muted/70 px-1.5 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 xl:px-3 xl:py-2"
      >
        <Icon icon="lucide:palette" className="h-4 w-4" />
        <span className="hidden xl:inline">{labels[theme]}</span>
      </button>

      {open && (
        <MenuPortal>
          <div
            ref={menuRef}
            style={menuStyle}
            className="z-[100] w-40 max-w-[calc(100vw-1.5rem)] rounded-lg border border-border/50 bg-card/95 p-2 shadow-soft"
          >
            {themes.map((id) => (
              <button
                key={id}
                type="button"
                onClick={async () => {
                  trackEvent({ category: "ui", action: "theme.switch", component: "ThemeMenu", details: { theme: id } });
                  await setTheme(id);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-xs transition-colors",
                  theme === id
                    ? "bg-muted/70 text-foreground"
                    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                )}
              >
                <span>{labels[id]}</span>
                {theme === id && <Icon icon="lucide:check" className="h-3 w-3" />}
              </button>
            ))}
          </div>
        </MenuPortal>
      )}
    </div>
  );
};
